/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import Collapse from "@mui/material/Collapse";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setPurpleSidenav,
  setGreenSidenav,
} from "context";

import logoQuadranteH from "assets/images/logo_koop/logo Quadrante H.png";

// --- CAMBIOS IMPLEMENTADOS PARA CONTROL DE ROLES ---
// 1. IMPORTA TUS HERRAMIENTAS DE ROLES Y AUTENTICACIÓN
// ¡¡¡IMPORTANTE!!! Asegúrate de que estas rutas sean correctas para tu proyecto.
import { canAccessPath } from "config/roleRoutes"; 
import { useAuth } from "context/authContext"; // O como obtengas la sesión del usuario
// --- FIN DE CAMBIOS ---

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    transparentSidenav,
    whiteSidenav,
    purpleSidenav,
    darkMode,
    sidenavColor,
    greenSidenav,
  } = controller;
  const location = useLocation();
  const collapseName = location.pathname.replace("/", "");
  
  // Estado para manejar qué menús están abiertos
  const [openMenus, setOpenMenus] = useState({});

  // --- CAMBIO IMPLEMENTADO PARA CONTROL DE ROLES ---
  // 2. OBTÉN EL ROL DEL USUARIO ACTUAL
  const { user, role, isAuthenticated } = useAuth(); // Agregado 'role' del contexto
  const userRole = role; // Usar directamente el 'role' del contexto
  // --- FIN DE CAMBIO ---

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  } else if (purpleSidenav) {
    textColor = "white";
  } else if (greenSidenav) {
    textColor = "white";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  // Función para toggle de submenus
  const toggleSubmenu = (key) => {
    setOpenMenus(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // FUNCIÓN MEJORADA: Detectar si una ruta está activa (exacta o dentro de submenús)
  const isRouteActive = (route) => {
    return location.pathname === route || location.pathname.startsWith(route + '/');
  };

  // Detectar si una ruta tiene submenus activos
  const isSubmenuActive = (collapse) => {
    return collapse && collapse.some(item => isRouteActive(item.route));
  };

  // --- CAMBIO IMPLEMENTADO PARA CONTROL DE ROLES ---
  // 3. CREA UNA FUNCIÓN RECURSIVA PARA FILTRAR LAS RUTAS
  const filterRoutes = (routesToFilter, role) => {
    // Si el rol aún no se ha cargado, no muestres ninguna ruta protegida.
    if (!role) {
      return routesToFilter.filter(r => r.key === 'sign-in' || r.key === 'sign-up');
    }

    return routesToFilter.reduce((acc, route) => {
      // Oculta las rutas de autenticación si el usuario ya está logueado.
      if (route.key === 'sign-in' || route.key === 'sign-up') {
        return acc;
      }

      // Si la ruta tiene sub-rutas (collapse), filtramos a los hijos.
      if (route.collapse) {
        const accessibleChildren = filterRoutes(route.collapse, role);
        // Solo mostramos el menú padre si tiene al menos un hijo accesible.
        if (accessibleChildren.length > 0) {
          acc.push({ ...route, collapse: accessibleChildren });
        }
      } 
      // Si es una ruta principal, verificamos el permiso.
      else if (route.route) {
        if (canAccessPath(role, route.route)) {
          acc.push(route);
        }
      } 
      // Si no es una ruta (ej. un título o divisor), lo mostramos siempre.
      else if (route.type === "title" || route.type === "divider") {
        acc.push(route);
      }
      
      return acc;
    }, []);
  };

  // Obtenemos la lista final de rutas que el usuario puede ver.
  const accessibleRoutes = useMemo(() => filterRoutes(routes, userRole), [routes, userRole]);
  // --- FIN DE CAMBIO ---

  // Inicializar menús abiertos basado en la ruta actual
  useEffect(() => {
    accessibleRoutes.forEach(route => {
      if (route.collapse && isSubmenuActive(route.collapse)) {
        setOpenMenus(prev => ({
          ...prev,
          [route.key]: prev[route.key] !== undefined ? prev[route.key] : true
        }));
      }
    });
  }, [location.pathname, accessibleRoutes]);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
      setPurpleSidenav(dispatch, window.innerWidth < 1200 ? false : purpleSidenav);
      setGreenSidenav(dispatch, window.innerWidth < 1200 ? false : greenSidenav);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Función para renderizar submenus
  const renderSubmenu = (collapse, parentKey, level = 1) => {
    return collapse.map((item) => {
      if (item.collapse && item.type === "collapse") {
        // Si el item tiene submenús anidados
        const isActive = isSubmenuActive(item.collapse) || isRouteActive(item.route);
        const isOpen = openMenus[item.key] !== undefined ? openMenus[item.key] : false;
        
        return (
          <div key={item.key}>
            <SidenavCollapse
              name={item.name}
              icon={item.icon}
              active={isActive}
              hasSubmenu={true}
              isOpen={isOpen}
              isSubmenu={level > 1}
              onClick={() => toggleSubmenu(item.key)}
            />
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: level * 1.5 }}>
                {renderSubmenu(item.collapse, item.key, level + 1)}
              </List>
            </Collapse>
          </div>
        );
      } else {
        // Item normal sin submenús anidados
        return (
          <NavLink key={item.key} to={item.route}>
            <SidenavCollapse 
              name={item.name} 
              icon={item.icon}
              active={isRouteActive(item.route)}
              isSubmenu={true}
            />
          </NavLink>
        );
      }
    });
  };

  // Render all the routes from the accessibleRoutes (Only the routes the user can access)
  const renderRoutes = accessibleRoutes.map(({ type, name, icon, title, noCollapse, key, href, route, collapse }) => {
    let returnValue;

    if (type === "collapse") {
      if (collapse) {
        // Item con submenu
        const isActive = isSubmenuActive(collapse);
        const isOpen = openMenus[key] !== undefined ? openMenus[key] : false;
        
        returnValue = (
          <div key={key}>
            <SidenavCollapse
              name={name}
              icon={icon}
              active={isActive}
              hasSubmenu={true}
              isOpen={isOpen}
              onClick={() => toggleSubmenu(key)}
            />
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 0.5 }}>
                {renderSubmenu(collapse, key)}
              </List>
            </Collapse>
          </div>
        );
      } else {
        // CORRECCIÓN CLAVE: Item normal sin submenu - usar isRouteActive en lugar de key === collapseName
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              name={name}
              icon={icon}
              active={isRouteActive(route)}
              noCollapse={noCollapse}
            />
          </Link>
        ) : (
          <NavLink key={key} to={route}>
            <SidenavCollapse 
              name={name} 
              icon={icon} 
              active={isRouteActive(route)} 
            />
          </NavLink>
        );
      }
    } else if (type === "title") {
      returnValue = (
        <MDTypography
          key={key}
          color={textColor}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </MDTypography>
      );
    } else if (type === "divider") {
      returnValue = (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav && !purpleSidenav && !greenSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    }

    return returnValue;
  });

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, purpleSidenav, miniSidenav, darkMode, greenSidenav }}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox component={NavLink} to="/" display="flex" alignItems="center">
          <MDBox
            width={!brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <MDBox component="img" src={logoQuadranteH} alt="Quadrante H Logo" width="120px" />
          </MDBox>
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav && !purpleSidenav && !greenSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>{renderRoutes}</List>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark", "green"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;